<template>
  <div class="flex flex-col gap-5">
    <div class="gradient px-6 py-4">
      <div class="flex flex-row justify-space-between">
        <div class="flex flex-col text-white">
          <h2 class="text-white">
            Simplifiez la gestion des renouvellements d’ordonnance
          </h2>
          <p>1. Filtrez les ordonnances à renouveler</p>
          <p>2. Créez un plan de renouvellement</p>
          <p>3. Votre patient recevra un rappel par SMS/notification aux dates renseignées 🎉 </p>
        </div>
        <Dialog v-model:open="showVideoModal">
          <DialogTrigger class="min-w-40">
            <img class="w-40 cursor-pointer" alt="tutoriel" :src="videoThumbnail">
          </DialogTrigger>
          <DialogContent class="min-w-[90%] max-h-auto">
            <DialogTitle>Gestion d'une commande Click & Collect</DialogTitle>
            <embed-video class="w-full" url="https://www.youtube.com/embed/DmP2f4GxNaI" />
          </DialogContent>
        </Dialog>
      </div>
    </div>
    <div class="flex flex-row justify-between pl-6">
      <div class="flex flex-col">
        <h1 class="text-2xl">
          Sélectionnez les ordonnances
        </h1>
        <span class="text-sm">Les filtres ci-dessous vous aident à organiser vos sessions</span>
      </div>
      <div class="flex flex-1 flex-row gap-x-2 mr-5 w-64 items-center justify-end">
        <div v-if="meta?.incoming_renewal_notification_count" class="flex flex-row gap-2 items-center text-sm">
          <Send class="w-4" />
          {{ meta.incoming_renewal_notification_count }}
          {{ pluralize('notification', meta.incoming_renewal_notification_count) }}
          {{ pluralize('prévue', meta.incoming_renewal_notification_count) }} cette semaine
        </div>
        <OngoingRenewalPlanButton :count="meta?.renewal_plan_consumer_count" :unique-consumers="true" />
      </div>
    </div>
    <PatientFilters v-if="meta" v-model="selectedTags" :tags="meta.tags" class="mx-6" />
    <base-flex-spinner v-if="isLoading" />
    <div v-else class="px-6 flex flex-col gap-3">
      <div class="flex justify-between items-center">
        <h2>Ordonnances</h2>
        <div class="flex flex-row gap-6">
          <BaseSelect
            v-model:model-value="dateFilter"
            default-value="today"
            trigger-class="w-58"
            placeholder="Toutes les ordonnances"
            :options="[
              {value: 'all', label: 'Toutes les ordonnances'},
              {value: 'this-month', label: 'Ordonnances du mois'},
              {value: 'this-week', label: 'Ordonnances de la semaine'},
              {value: 'yesterday', label: 'Ordonnances de la veille'},
              {value: 'today', label: 'Ordonnances du jour'},
            ]"
          />
          <BaseSelect
            v-model:model-value="planFilter"
            default-value="to-renew"
            trigger-class="w-58"
            placeholder="Toutes les ordonnances"
            :options="[
              {value: 'all', label: 'Toutes les ordonnances'},
              {value: 'to-renew', label: 'Ordonnances à renouveler'},
              {value: 'with-plan', label: 'Ordonnances avec plan'},
              {value: 'non-renewable', label: 'Ordonnances non renouvelables'},
            ]"
          />
        </div>
      </div>
      <prescription-list :prescriptions="prescriptions" />
      <div class="flex justify-center">
        <base-button
          v-if="!!meta?.after_key && !endReached"
          class="font-medium"
          :disabled="isLoadingMore"
          @click="loadMore"
        >
          <base-spinner v-if="isLoadingMore" size="button" />
          <span v-else>Voir plus</span>
        </base-button>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import {onMounted, ref, watch} from "vue";
import {prescriptionApi} from '@/container';
import PrescriptionList from '@/prescription/components/PrescriptionList.vue';
import {Prescription, PrescriptionIndexMeta, PrescriptionIndexParams} from "@/prescription/interfaces/Prescription";
import BaseFlexSpinner from "@/core/components/base/spinner/BaseFlexSpinner.vue";
import BaseSpinner from "@/core/components/base/spinner/BaseSpinner.vue";
import BaseButton from "@/core/components/base/BaseButton.vue";
import PatientFilters, {PatientFiltersProps} from "@/prescription/components/PatientFilters.vue";
import BaseSelect from "@/core/components/base/BaseSelect.vue";
import OngoingRenewalPlanButton from "@/prescription/components/OngoingRenewalPlanButton.vue";
import videoThumbnail from "@/assets/images/thumbnail_video_default.png";
import {Dialog, DialogContent, DialogTitle, DialogTrigger} from "@/core/components/ui/dialog";
import EmbedVideo from "@/core/components/video/EmbedVideo.vue";
import {pluralize} from "@/core/filters/Pluralize";
import {Send} from "@lucide/vue";
import dayjs from "dayjs";

const isLoading = ref<boolean>(false);
const isLoadingMore = ref<boolean>(false);
const endReached = ref<boolean>(false);
const showVideoModal = ref<boolean>(false);
const selectedTags = ref<PatientFiltersProps>({
    tags: [],
    certified: null,
    children: null,
    tags_operator: 'AND',
    purchase_type: null,
    purchase_id: null,
    gist: null,
});
const meta = ref<PrescriptionIndexMeta | null>(null);
const prescriptions = ref<Prescription[]>([]);
const planFilter = ref<'without-plan' | 'with-plan' | 'non-renewable' | 'all' | 'to-renew'>('to-renew');
const dateFilter = ref<'all' | 'today' | 'yesterday' | 'this-week' | 'this-month'>('all');

onMounted(() => {
    refresh();
});

watch(selectedTags, () => {
    refresh();
}, {deep: true});

watch(planFilter, () => {
    refresh();
}, {deep: true});

watch(dateFilter, () => {
    refresh();
}, {deep: true});

const refresh = () => {
    isLoading.value = true;
    prescriptions.value = [];
    isLoadingMore.value = false;
    endReached.value = false;
    if (meta.value) {
        meta.value.after_key = null;
    }
    fetch().finally(() => isLoading.value = false);
};

const fetch = (async () => {
    const params: PrescriptionIndexParams = {
        with_renewal_plan_count: true,
        limit: 20,
        tags: selectedTags.value.tags,
        tags_operator: 'AND',
        after: meta.value?.after_key ? JSON.stringify(meta.value.after_key) : '',
        ...selectedTags.value.children && {age_to: '19 years'},
        ...selectedTags.value.certified && {certified: 1},
        ...selectedTags.value.purchase_type && {purchase_type: selectedTags.value.purchase_type},
        ...selectedTags.value.purchase_id && {purchase_id: selectedTags.value.purchase_id},
        ...selectedTags.value.gist && {gist: 1}
    };

    switch (planFilter.value) {
        case 'with-plan':
            params.renewal_plan = 'any';
            break;
        case 'without-plan':
            params.renewal_plan = 'none';
            break;
        case 'non-renewable':
            params.renewable = false;
            break;
        case 'to-renew':
            params.renewable = true;
            params.renewal_plan = 'none';
            break;
    }

    switch (dateFilter.value) {
        case 'today':
            params.from = dayjs().format('YYYY-MM-DD');
            break;
        case 'yesterday':
            params.from = dayjs().subtract(1, 'day').format('YYYY-MM-DD');
            params.to = dayjs().subtract(1, 'day').format('YYYY-MM-DD');
            break;
        case 'this-week':
            params.from = dayjs().startOf('week').format('YYYY-MM-DD');
            break;
        case 'this-month':
            params.from = dayjs().startOf('month').format('YYYY-MM-DD');
            break;
    }

    return await prescriptionApi()
        .index(params)
        .then(response => {
            prescriptions.value = [...prescriptions.value, ...response.data];
            endReached.value = meta.value?.after_key === response.meta.after_key;
            meta.value = response.meta;
        });
});

const loadMore = () => {
    isLoadingMore.value = true;
    fetch().finally(() => isLoadingMore.value = false);
};
</script>
